import React, { useState, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  Typography,
  Box,
  CircularProgress
} from '@material-ui/core'
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import { getCroppedImg } from 'utils/imageCrop'
import { toast } from 'utils/notify'

const ImageCropModal = ({
  open,
  onClose,
  imageSrc,
  fileName,
  onCropComplete,
  title = 'Crop Profile Picture'
}) => {
  const classes = useStyles()
  const [crop, setCrop] = useState({
    unit: '%',
    width: 50,
    height: 50,
    x: 25,
    y: 25
  })
  const [completedCrop, setCompletedCrop] = useState(null)
  const [imgRef, setImgRef] = useState(null)
  const [loading, setLoading] = useState(false)

  const onImageLoaded = useCallback((img) => {
    if (img) {
      setImgRef(img)
      return false // Return false to prevent default behavior
    }
  }, [])

  const handleSave = async () => {
    try {
      setLoading(true)
      if (!completedCrop || !imgRef) {
        toast.error('Adjust the crop area before saving.')
        return
      }

      const scaleX = imgRef.naturalWidth / imgRef.width
      const scaleY = imgRef.naturalHeight / imgRef.height
      const scaledCrop = {
        unit: 'px',
        x: completedCrop.x * scaleX,
        y: completedCrop.y * scaleY,
        width: completedCrop.width * scaleX,
        height: completedCrop.height * scaleY
      }
      const croppedImage = await getCroppedImg(imgRef, scaledCrop, 320, fileName)
      onCropComplete(croppedImage)
      // Don't call onClose here - let parent component handle it
    } catch (error) {
      console.error('Error cropping image:', error)
      toast.error('Image crop failed. Try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    setCrop({
      unit: '%',
      width: 50,
      height: 50,
      x: 25,
      y: 25
    })
    setCompletedCrop(null)
    setImgRef(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary" className={classes.subtitle}>
          Adjust the image to create a square profile picture
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box className={classes.cropContainer}>
          {imageSrc && (
            <ReactCrop
              crop={crop}
              onChange={(newCrop) => setCrop(newCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={1}
              minWidth={50}
              minHeight={50}>
              <img
                ref={onImageLoaded}
                src={imageSrc}
                alt="Crop preview"
                onLoad={(e) => {
                  // Calculate initial square crop when image loads
                  const { naturalWidth, naturalHeight } = e.target
                  const size = Math.min(naturalWidth, naturalHeight)
                  const x = (naturalWidth - size) / 2
                  const y = (naturalHeight - size) / 2

                  setCrop({
                    unit: 'px',
                    width: size * 0.8,
                    height: size * 0.8,
                    x: x + size * 0.1,
                    y: y + size * 0.1
                  })
                }}
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  display: 'block'
                }}
              />
            </ReactCrop>
          )}
        </Box>

        <Box className={classes.controls}>
          <Typography variant="body2" color="textSecondary">
            Drag the corners to resize the crop area
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={handleCancel} disabled={loading} className={classes.cancelButton}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading || !completedCrop}
          className={classes.saveButton}
          startIcon={loading && <CircularProgress size={16} />}>
          {loading ? 'Cropping...' : 'Save & Use'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImageCropModal

const useStyles = makeStyles(() => ({
  title: {
    fontWeight: 600,
    color: '#15b4db'
  },
  subtitle: {
    marginTop: '4px',
    fontSize: '13px'
  },
  cropContainer: {
    width: '100%',
    backgroundColor: '#f5f5f5',
    borderRadius: '8px',
    overflow: 'hidden',
    display: 'flex',
    justifyContent: 'center'
  },
  controls: {
    marginTop: '16px',
    textAlign: 'center'
  },
  actions: {
    padding: '16px 24px',
    gap: '12px'
  },
  cancelButton: {
    textTransform: 'none',
    color: '#666'
  },
  saveButton: {
    textTransform: 'none',
    backgroundColor: '#15b4db',
    '&:hover': {
      backgroundColor: '#1290a8'
    },
    '&:disabled': {
      backgroundColor: '#cccccc'
    }
  }
}))
